/**
 * @fileoverview Card component for content containers.
 * Provides structured layout with header, content, and footer sections.
 * @module components/ui/card
 */

import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Card container component with rounded corners and shadow.
 * @param props - HTML div props
 * @param ref - Forwarded ref to the div element
 */
const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "shadcn-card rounded-xl border bg-card border-card-border text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
));
Card.displayName = "Card"

/**
 * Card header section with vertical spacing.
 * Typically contains CardTitle and CardDescription.
 * @param props - HTML div props
 * @param ref - Forwarded ref to the div element
 */
const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader"

/**
 * Card title component with large, bold text.
 * @param props - HTML div props
 * @param ref - Forwarded ref to the div element
 */
const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

/**
 * Card description for secondary text below title.
 * @param props - HTML div props
 * @param ref - Forwarded ref to the div element
 */
const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription"

/**
 * Card content section with padding (no top padding).
 * @param props - HTML div props
 * @param ref - Forwarded ref to the div element
 */
const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

/**
 * Card footer section with horizontal flex layout.
 * Typically contains action buttons.
 * @param props - HTML div props
 * @param ref - Forwarded ref to the div element
 */
const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"
export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
}
